import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useExpiringLocalStorage } from "@redotech/react-util/local-storage";
import { useMediaQuery } from "@redotech/react-util/media-query";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantAuthRpcClientContext } from "@redotech/redo-merchant-app-common/auth";
import { useIsHistoryTraversal } from "@redotech/redo-merchant-app-common/history-traversal";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  DEVICE_AUTHENTICATION_TOKEN_KEY,
  DEVICE_ID_KEY,
  DEVICE_REFRESH_TOKEN_KEY,
} from "@redotech/redo-web/authentication/authentication-settings";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export const MOBILE_THRESHOLD = 499;
export const TABLET_THRESHOLD = 1024;
const COARSE_POINTER_QUERY = "(pointer: coarse)";
export const FORCE_DESKTOP_KEY = "redo-force-desktop-mode";
export const PREFER_WMS_KEY = "redo-prefer-wms";
export const LAUNCH_TARGET_KEY = "redo-mobile-launch-target";

export type LaunchTarget = "wms" | "scan";

export function readSavedLaunchTarget(): LaunchTarget | null {
  const saved = localStorage.getItem(LAUNCH_TARGET_KEY);
  if (saved === "wms" || saved === "scan") {
    return saved;
  }
  return localStorage.getItem(PREFER_WMS_KEY) === "true" ? "wms" : null;
}

export function useForceDesktopMode() {
  const [forceDesktop, setForceDesktop] = useState(() => {
    return localStorage.getItem(FORCE_DESKTOP_KEY) === "true";
  });

  const setForceDesktopMode = (value: boolean, persist: boolean) => {
    if (persist) {
      localStorage.setItem(FORCE_DESKTOP_KEY, value ? "true" : "false");
    }
    setForceDesktop(value);
  };

  const clearPersistedPreference = () => {
    localStorage.removeItem(FORCE_DESKTOP_KEY);
    localStorage.removeItem(PREFER_WMS_KEY);
    localStorage.removeItem(LAUNCH_TARGET_KEY);
    setForceDesktop(false);
  };

  return { forceDesktop, setForceDesktopMode, clearPersistedPreference };
}

/**
 * Tablets report a coarse pointer, so a narrow desktop window keeps the
 * desktop layout while an iPad at the same width does not.
 */
export function useIsTabletOrSmaller(): boolean {
  const isPhoneWidth = useScreenType(MOBILE_THRESHOLD) === ScreenType.MOBILE;
  const isTabletWidth = useScreenType(TABLET_THRESHOLD) === ScreenType.MOBILE;
  const hasCoarsePointer = useMediaQuery(COARSE_POINTER_QUERY);
  return isPhoneWidth || (isTabletWidth && hasCoarsePointer);
}

export const MobileLauncher = memo(function MobileLauncher({
  onSwitchToDesktop,
  wmsEnabled,
  wmsSourceOfTruthEnabled,
  pinLoginEnabled,
  teamId,
  userName,
  canScanToVerify = false,
  autoLaunchSavedTarget = false,
}: {
  onSwitchToDesktop: (remember: boolean) => void;
  wmsEnabled: boolean;
  wmsSourceOfTruthEnabled: boolean;
  pinLoginEnabled: boolean;
  teamId: string;
  userName: string | undefined;
  canScanToVerify?: boolean;
  autoLaunchSavedTarget?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isHistoryTraversal = useIsHistoryTraversal();
  const routes = useRequiredContext(MerchantRoutesContext);
  const authRpcClient = useRequiredContext(RedoMerchantAuthRpcClientContext);
  const [rememberChoice, setRememberChoice] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [showDeviceSetup, setShowDeviceSetup] = useState(false);
  const [deviceSetupComplete, setDeviceSetupComplete] = useState(false);

  const [, setAccessToken] = useExpiringLocalStorage<string | undefined>(
    DEVICE_AUTHENTICATION_TOKEN_KEY(teamId),
    undefined,
  );

  const hasRefreshToken = useMemo(() => {
    const token = localStorage.getItem(DEVICE_REFRESH_TOKEN_KEY(teamId));
    return !!token;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, deviceSetupComplete]);

  const needsDeviceSetup = pinLoginEnabled && !hasRefreshToken;

  const [enableDeviceResult, handleEnableDevice] = useTriggerLoad(async () => {
    if (!deviceName.trim()) {
      return;
    }

    try {
      const result = await authRpcClient.enableDeviceAuth({
        deviceName: deviceName.trim(),
        userAgent: navigator.userAgent,
      });

      localStorage.setItem(
        DEVICE_REFRESH_TOKEN_KEY(teamId),
        result.refreshToken,
      );
      localStorage.setItem(DEVICE_ID_KEY(teamId), result.deviceId);
      setAccessToken(result.accessToken, result.expiresAt);

      setShowDeviceSetup(false);
      setDeviceName("");
      setDeviceSetupComplete(true);

      toast(
        t('Device "{{deviceName}}" has been set up for PIN authentication.', {
          deviceName,
        }),
        { variant: "success" },
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("Failed to set up device");
      toast(message, { variant: "error" });
    }
  });

  const launchTargetHref = useCallback(
    (target: LaunchTarget): string => {
      switch (target) {
        case "wms":
          return wmsSourceOfTruthEnabled
            ? routes.warehouseHref
            : routes.getOrdersOutboundLabelsSubRoute("pick-sessions");
        case "scan":
          return routes.getOrdersOutboundLabelsSubRoute("scan");
        default:
          return assertNever(target);
      }
    },
    [routes, wmsSourceOfTruthEnabled],
  );

  const handleLaunch = (target: LaunchTarget) => {
    if (rememberChoice) {
      localStorage.setItem(LAUNCH_TARGET_KEY, target);
    }
    navigate(launchTargetHref(target));
  };

  const launchTargetAllowed = useCallback(
    (target: LaunchTarget): boolean =>
      target === "scan" ? canScanToVerify : wmsEnabled,
    [canScanToVerify, wmsEnabled],
  );

  useEffect(() => {
    if (!autoLaunchSavedTarget || isHistoryTraversal) {
      return;
    }
    const saved = readSavedLaunchTarget();
    if (saved && launchTargetAllowed(saved)) {
      navigate(launchTargetHref(saved));
    }
  }, [
    autoLaunchSavedTarget,
    isHistoryTraversal,
    launchTargetAllowed,
    launchTargetHref,
    navigate,
  ]);

  const hasWarehouseEntry = wmsEnabled || canScanToVerify;

  if (showDeviceSetup) {
    return (
      <Flex
        align="center"
        bgColor="primary"
        bottom="0"
        dir="column"
        gap="xl"
        grow={1}
        justify="center"
        left="0"
        p="3xl"
        position="fixed"
        right="0"
        top="0"
        zIndex="overlay"
      >
        <Text fontSize="xl" fontWeight="semibold">
          {t("Set up this device")}
        </Text>
        <Text fontSize="sm" maxW="xxs" textAlign="center" textColor="secondary">
          {t("Give this device a name to enable PIN authentication.")}
        </Text>
        <Flex dir="column" gap="md" maxW="xxs" w="full">
          <RedoTextInput
            placeholder={t("e.g., Warehouse iPad")}
            setValue={setDeviceName}
            size="lg"
            value={deviceName}
          />
          <RedoButton
            disabled={!deviceName.trim() || enableDeviceResult.pending}
            hierarchy="primary"
            onClick={() => handleEnableDevice()}
            pending={enableDeviceResult.pending}
            size="lg"
            text={t("Set up device")}
          />
          <RedoButton
            disabled={enableDeviceResult.pending}
            hierarchy="tertiary"
            onClick={() => setShowDeviceSetup(false)}
            size="lg"
            text={t("Cancel")}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      bgColor="primary"
      bottom="0"
      dir="column"
      gap="xl"
      grow={1}
      justify="center"
      left="0"
      p="3xl"
      position="fixed"
      right="0"
      top="0"
      zIndex="overlay"
    >
      <Text fontSize="xl" fontWeight="semibold">
        {userName
          ? t("Welcome back, {{userName}}", { userName })
          : t("Welcome to {{brand}}", { brand: "Redo" })}
      </Text>
      <Text fontSize="sm" maxW="xxs" textAlign="center" textColor="secondary">
        {hasWarehouseEntry
          ? t(
              "This page is optimized for larger screens. You can access the WMS app or switch to desktop mode.",
            )
          : t(
              "This page is optimized for larger screens. You can switch to desktop mode.",
            )}
      </Text>
      <Flex dir="column" gap="md" maxW="xxs" w="full">
        {wmsEnabled && (
          <RedoButton
            hierarchy="primary"
            onClick={() => handleLaunch("wms")}
            size="lg"
            text={t("Go to WMS")}
          />
        )}
        {canScanToVerify && (
          <RedoButton
            hierarchy={wmsEnabled ? "secondary" : "primary"}
            onClick={() => handleLaunch("scan")}
            size="lg"
            text={t("Scan to verify")}
          />
        )}
        {wmsEnabled && !wmsSourceOfTruthEnabled && (
          <RedoButton
            hierarchy="secondary"
            onClick={() => navigate(`${routes.warehouseHref}/bin-assignment`)}
            size="lg"
            text={t("Bin assignment")}
          />
        )}
        {needsDeviceSetup && (
          <RedoButton
            hierarchy={hasWarehouseEntry ? "secondary" : "primary"}
            onClick={() => setShowDeviceSetup(true)}
            size="lg"
            text={t("Set up device for PIN login")}
          />
        )}
        <RedoButton
          hierarchy={
            hasWarehouseEntry || needsDeviceSetup ? "tertiary" : "primary"
          }
          onClick={() => onSwitchToDesktop(rememberChoice)}
          size="lg"
          text={t("Switch to desktop mode")}
        />
        <Flex align="center" justify="center">
          <RedoCheckbox
            label={t("Remember my choice")}
            setValue={(value) => setRememberChoice(value === true)}
            value={rememberChoice}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});
